import Dropdown from "common/ui/Dropdown";
import { useState } from "react";

import { validatePhone, validatePostalCode } from "config/config";
import stateMappings from "config/states";

import {
  Button,
  Checkbox,
  Icon,
  PhoneInputField,
  TextField,
} from "@/design-system";

const initialFormState = {
  fullName: "",
  address: "",
  address2: "",
  city: "",
  state: "ON",
  zip: "",
  phone: "",
  isResidential: false,
  country: "ca",
};

/**
 * The checkout's add-address form, inline inside the Ship to / Bill to cards —
 * the reference's `coShipFormOpen` panel (grep it in
 * `Dens N Dente Tailwind.dc.html`), which replaced the old `AddressModal`
 * (now dead). Same two-column TextField grid, "Set as my default address"
 * checkbox, and the footer with the remember-it hint beside Cancel /
 * Save address.
 *
 * The field set is the app's, not the prototype's five: the NetSuite payload
 * and the tax/shipping estimate need province, country and phone, so those
 * stay. Field behaviour is the reference's exactly:
 *
 * - blur-validated per field via a wrapping div (TextField's own `onBlur` is
 *   its focus ring), typing clears that field's error;
 * - Save re-validates everything and bumps `errorNonce`, so still-invalid
 *   fields shake on every attempt;
 * - `makeDefault` travels to `onSave` on the form data — the caller strips it
 *   before the address reaches any payload.
 *
 * `onSave(formData, addressType)` is only called once valid; the parent
 * appends + selects + toasts (`handleSaveAddress`) and closes the form.
 */
export default function AddressForm({ addressType, onSave, onCancel }) {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [errorNonce, setErrorNonce] = useState(0);
  const [makeDefault, setMakeDefault] = useState(false);

  const fieldError = (name, data) => {
    if (name === "fullName") return data.fullName ? "" : "Enter the full name";
    if (name === "address")
      return data.address ? "" : "Enter the street address";
    if (name === "city") return data.city ? "" : "Enter the city";
    if (name === "state") return data.state ? "" : "Select a province";
    if (name === "zip") return validatePostalCode(data.zip, data.country) || "";
    if (name === "phone") {
      if (!data.phone) return "Enter a phone number";
      return validatePhone(data.phone)
        ? ""
        : "Enter a valid 10-digit phone number";
    }
    return "";
  };

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Typing is fixing — clear the field's error immediately, like the reference.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  };

  const handleChange = (e) => updateField(e.target.name, e.target.value);

  // Blur bubbles from the input to this wrapper handler (TextField consumes the
  // input's own onBlur for its focus ring).
  const validateOnBlur = (name) => () =>
    setErrors((prev) => {
      const message = fieldError(name, formData);
      return message ? { ...prev, [name]: message } : prev;
    });

  const handleSave = () => {
    const newErrors = {};
    ["fullName", "address", "city", "state", "zip", "phone"].forEach((name) => {
      const message = fieldError(name, formData);
      if (message) newErrors[name] = message;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // One bump per attempt: every still-invalid field shakes again.
      setErrorNonce((nonce) => nonce + 1);
      return;
    }
    onSave({ ...formData, makeDefault }, addressType);
  };

  const states = stateMappings[formData.country?.toLowerCase?.()] || {};
  const stateOptions = Object.entries(states).map(([label, value]) => ({
    label: label
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
    value,
  }));

  const countryOptions = [{ label: "Canada", value: "ca" }];

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[var(--space-4)]">
        <div onBlur={validateOnBlur("fullName")}>
          <TextField
            label="Full name"
            name="fullName"
            required
            placeholder="First and last name"
            value={formData.fullName}
            onChange={handleChange}
            error={errors.fullName}
            errorNonce={errorNonce}
          />
        </div>
        <div onBlur={validateOnBlur("phone")}>
          <PhoneInputField
            name="phone"
            required
            value={formData.phone}
            onChange={(phone) => updateField("phone", phone)}
            error={errors.phone}
            errorNonce={errorNonce}
          />
        </div>
        <div className="col-[1_/_-1]" onBlur={validateOnBlur("address")}>
          <TextField
            label="Street address"
            name="address"
            required
            placeholder="Street, unit"
            value={formData.address}
            onChange={handleChange}
            error={errors.address}
            errorNonce={errorNonce}
          />
        </div>
        <TextField
          label="Apt or suite"
          name="address2"
          placeholder="Apt. 3 or Suite #1516"
          value={formData.address2}
          onChange={handleChange}
        />
        <div onBlur={validateOnBlur("city")}>
          <TextField
            label="City"
            name="city"
            required
            value={formData.city}
            onChange={handleChange}
            error={errors.city}
            errorNonce={errorNonce}
          />
        </div>
        <Dropdown
          label="Province *"
          name="state"
          value={formData.state}
          onChange={handleChange}
          options={stateOptions}
          error={errors.state}
        />
        <div onBlur={validateOnBlur("zip")}>
          <TextField
            label="Postal code"
            name="zip"
            required
            placeholder="A1A 1A1"
            value={formData.zip}
            onChange={handleChange}
            error={errors.zip}
            errorNonce={errorNonce}
          />
        </div>
        <Dropdown
          label="Country *"
          name="country"
          value={formData.country}
          onChange={handleChange}
          options={countryOptions}
        />
      </div>

      <Checkbox
        shape="round"
        label="This is a residential address"
        checked={formData.isResidential}
        onChange={(checked) =>
          setFormData((prev) => ({ ...prev, isResidential: checked }))
        }
      />
      <Checkbox
        shape="round"
        label="Set as my default address"
        checked={makeDefault}
        onChange={(checked) => setMakeDefault(Boolean(checked))}
      />

      <div className="flex flex-wrap items-center justify-between gap-[var(--space-4)]">
        <div className="flex items-center gap-[8px] text-xs text-ink-3">
          <Icon name="autorenew" size={15} />
          We&rsquo;ll use this address for this order and remember it for next
          time.
        </div>
        <div className="flex items-center gap-[var(--space-2)]">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" startIcon="check" onClick={handleSave}>
            Save address
          </Button>
        </div>
      </div>
    </div>
  );
}
